/* eslint-disable */
import React, { useRef, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
    User,
    FileText,
    Database,
    Sun,
    Moon,
    LogOut,
    HelpCircle,
    Download,
} from 'lucide-react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from '@mui/material'
import { logout } from '../../redux'
import './ProfilePopover.css'

function ProfilePopover({ isOpen, onClose, user }) {
    const dispatch = useDispatch()
    const themeMode = useSelector((state) => state.theme?.mode || 'light')
    const popoverRef = useRef(null)
    const [showInstallGuide, setShowInstallGuide] = useState(false)

    const isIOS =
        typeof navigator !== 'undefined' &&
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !window.MSStream

    const isStandalone =
        typeof window !== 'undefined' &&
        (Boolean(window.isPWAInstalled) ||
            window.matchMedia('(display-mode: standalone)').matches ||
            window.navigator.standalone === true)

    const handleDownloadPWA = async () => {
        if (isStandalone) {
            setShowInstallGuide(true)
            onClose()
            return
        }

        const deferredPrompt = window.deferredPWAInstallPrompt

        if (deferredPrompt) {
            try {
                deferredPrompt.prompt()
                const { outcome } = await deferredPrompt.userChoice
                if (outcome === 'accepted') {
                    window.deferredPWAInstallPrompt = null
                }
            } catch (err) {
                console.error('Error triggering PWA prompt:', err)
                setShowInstallGuide(true)
            }
            onClose()
        } else {
            setShowInstallGuide(true)
            onClose()
        }
    }

    useEffect(() => {
        if (!isOpen) return

        const handleOutsideClick = (event) => {
            if (event.target.closest('#avatarBtn')) {
                return
            }
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target)
            ) {
                onClose()
            }
        }

        document.addEventListener('click', handleOutsideClick, true)
        return () => {
            document.removeEventListener('click', handleOutsideClick, true)
        }
    }, [isOpen, onClose])

    const toggleTheme = () => {
        dispatch({ type: 'TOGGLE_THEME_MODE' })
    }

    const handleLogout = () => {
        dispatch(logout())
        onClose()
    }

    const name = user
        ? `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
          user.name ||
          'User Profile'
        : 'User Profile'
    const email = user?.email || 'user@example.com'

    const getInitials = (nameStr) => {
        if (!nameStr || nameStr === 'User Profile') return 'UP'
        const parts = nameStr.split(' ').filter(Boolean)
        if (parts.length === 0) return 'UP'
        if (parts.length === 1) return parts[0][0].toUpperCase()
        return (parts[0][0] + parts[1][0]).toUpperCase()
    }

    return (
        <>
            <div
                ref={popoverRef}
                id="profilePopover"
                className={isOpen ? 'open' : ''}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="popover-header">
                    <div className="popover-avatar-initials">
                        {getInitials(name)}
                    </div>
                    <div className="popover-user-info">
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <span className="popover-user-name">{name}</span>
                            {(user?.role === 'Instructor' ||
                                String(user?.role || '').toUpperCase() ===
                                    'ADMIN') && (
                                <span className="popover-instructor-tag">
                                    {String(user?.role || '').toUpperCase() ===
                                    'ADMIN'
                                        ? 'ADMIN'
                                        : 'Instructor'}
                                </span>
                            )}
                        </div>
                        <span className="popover-user-email">{email}</span>
                    </div>
                </div>
                <div className="popover-menu">
                    <Link
                        className="popover-menu-item"
                        onClick={onClose}
                        to="/myprofile"
                    >
                        <User />
                        <span>My Profile</span>
                    </Link>
                    <Link
                        className="popover-menu-item"
                        onClick={onClose}
                        to="/notes"
                    >
                        <FileText />
                        <span>My Notes</span>
                    </Link>
                    <Link
                        className="popover-menu-item"
                        onClick={onClose}
                        to="/question-creator-dashboard"
                    >
                        <Database />
                        <span>My Question Bank</span>
                    </Link>

                    <div className="popover-divider" />

                    <button
                        className="popover-menu-item"
                        onClick={toggleTheme}
                        type="button"
                    >
                        {themeMode === 'light' ? (
                            <>
                                <Moon />
                                <span>Dark Mode</span>
                            </>
                        ) : (
                            <>
                                <Sun />
                                <span>Light Mode</span>
                            </>
                        )}
                    </button>

                    <button
                        className="popover-menu-item"
                        onClick={() => {
                            window.dispatchEvent(
                                new CustomEvent('show-tutorial')
                            )
                            onClose()
                        }}
                        type="button"
                    >
                        <HelpCircle />
                        <span>Website Guide</span>
                    </button>

                    <button
                        className="popover-menu-item"
                        onClick={handleDownloadPWA}
                        type="button"
                    >
                        <Download />
                        <span>Install as an App</span>
                    </button>

                    <div className="popover-divider" />
                </div>
                <div className="popover-footer">
                    <button
                        className="popover-menu-item logout"
                        onClick={handleLogout}
                        type="button"
                    >
                        <LogOut />
                        <span>Logout</span>
                    </button>
                </div>
            </div>
            <Dialog
                fullWidth
                maxWidth="xs"
                onClose={() => setShowInstallGuide(false)}
                open={showInstallGuide}
                PaperProps={{
                    sx: {
                        bgcolor: 'var(--bg-card, #ffffff)',
                        color: 'var(--text-dark, #111111)',
                        borderRadius: '16px',
                        p: 1.5,
                        boxShadow:
                            'var(--shadow-popover, 0 10px 30px rgba(0,0,0,0.15))',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: '1.15rem',
                        pb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        color: 'var(--text-dark, #111111)',
                    }}
                >
                    <Download
                        size={22}
                        style={{ color: 'var(--primary, #007bff)' }}
                    />
                    Install MyAcademic
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    {isStandalone ? (
                        <Typography
                            sx={{
                                color: 'var(--text-muted, #666666)',
                                fontSize: '0.95rem',
                            }}
                            variant="body2"
                        >
                            MyAcademic is already installed and running as an
                            app on your device!
                        </Typography>
                    ) : isIOS ? (
                        <Box
                            sx={{
                                color: 'var(--text-muted, #666666)',
                                fontSize: '0.92rem',
                                lineHeight: 1.7,
                            }}
                        >
                            <Typography
                                sx={{
                                    mb: 1,
                                    fontWeight: 600,
                                    color: 'var(--text-dark, #111111)',
                                }}
                                variant="body2"
                            >
                                Install MyAcademic on iPhone / iPad:
                            </Typography>
                            1. Tap the <strong>Share</strong> button at the
                            bottom of Safari.
                            <br />
                            2. Scroll down and tap{' '}
                            <strong>Add to Home Screen</strong>.
                            <br />
                            3. Tap <strong>Add</strong> in the top-right corner.
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                color: 'var(--text-muted, #666666)',
                                fontSize: '0.92rem',
                                lineHeight: 1.7,
                            }}
                        >
                            <Typography
                                sx={{
                                    mb: 1,
                                    fontWeight: 600,
                                    color: 'var(--text-dark, #111111)',
                                }}
                                variant="body2"
                            >
                                Install MyAcademic on your device:
                            </Typography>
                            1. Look for the <strong>Install</strong> icon (⬇️ or
                            🖥️) in your browser&apos;s address bar.
                            <br />
                            2. Or open the browser menu (<strong>
                                ⋮
                            </strong> or <strong>…</strong>) and select{' '}
                            <strong>&quot;Install MyAcademic&quot;</strong> or{' '}
                            <strong>&quot;Add to Home screen&quot;</strong>.
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ pt: 1, pb: 1, px: 2 }}>
                    <Button
                        onClick={() => setShowInstallGuide(false)}
                        sx={{
                            bgcolor: 'var(--primary, #007bff)',
                            color: '#ffffff',
                            textTransform: 'none',
                            borderRadius: '8px',
                            fontWeight: 600,
                            px: 2.5,
                            '&:hover': {
                                bgcolor: 'var(--primary-hover, #0056b3)',
                            },
                        }}
                        variant="contained"
                    >
                        Got it
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default ProfilePopover
